import { isMissing, isNumericType } from "@/profitability/missing";
import { rateColumn } from "@/profitability/records/judgement";
import { roleColumn } from "@/profitability/records/objects";
import type {
  ReportColumn,
  ReportDataset,
  ReportRow,
} from "@/profitability/types";

export interface MissingFields {
  /** Missing for a reason of their own: nothing else on the row explains them. */
  gaps: ReportColumn[];
  /**
   * The figures that can't be worked out because a gap feeds them: the
   * result when a cost or the total is missing, the rate when the result or
   * the total is.
   */
  blocked: ReportColumn[];
  /** The input every gap comes from (`source`), when they all name one. */
  source: string | null;
}

/**
 * The row's figures that are missing. An estimate or a target with no value
 * is not missing: a job may have no estimate, and a target is the owner's,
 * not the files'. Nor is the rate of a row whose total is a real zero (a
 * callback that brought in nothing): there is nothing to take it of.
 */
export const absentColumns = (dataset: ReportDataset, row: ReportRow) => {
  const total = roleColumn(dataset, "total");
  const rate = rateColumn(dataset);
  const ofNothing =
    !!total && !isMissing(row[total.key]) && Number(row[total.key]) === 0;
  return dataset.columns.filter(
    (column) =>
      isNumericType(column.type) &&
      !column.estimate_of &&
      column.role !== "target" &&
      !(ofNothing && column.key === rate?.key) &&
      isMissing(row[column.key]),
  );
};

/** Whether one of the row's figures is missing (`absentColumns`). */
export const isAbsent = (
  dataset: ReportDataset,
  row: ReportRow,
  column: ReportColumn,
) => absentColumns(dataset, row).some((item) => item.key === column.key);

/**
 * A row's missing figures, read from the roles the report declares: costs
 * (and the total) feed the result and a subtotal; the result and the total
 * feed the rate. For a run that did not trace how its figures were made;
 * one that did says it per figure (`figureGaps`).
 */
export const missingFields = (
  dataset: ReportDataset,
  row: ReportRow,
): MissingFields => {
  const absent = absentColumns(dataset, row);
  const missing = new Set(absent.map((column) => column.key));
  const lacks = (column: ReportColumn | undefined) =>
    !!column && missing.has(column.key);
  const total = roleColumn(dataset, "total");
  const result = roleColumn(dataset, "result");
  const cost = dataset.columns.some(
    (column) => column.role === "cost" && lacks(column),
  );
  const rate = rateColumn(dataset);
  const fed = (column: ReportColumn) => {
    if (column.role === "result") return cost || lacks(total);
    if (column.role === "subtotal") return cost;
    if (column === rate) return lacks(result) || lacks(total);
    return false;
  };
  const gaps = absent.filter((column) => !fed(column));
  const blocked = absent.filter(fed);
  const sources = new Set(gaps.map((column) => column.source ?? ""));
  const [only] = [...sources];
  return {
    gaps,
    blocked,
    source: sources.size === 1 && only ? only : null,
  };
};

export const columnWords = (column: ReportColumn) =>
  column.label ?? column.key.replaceAll("_", " ");

/** Past three names, the rest are a count. */
export const listWords = (words: string[]) => {
  if (words.length <= 1) return words[0] ?? "";
  if (words.length > 3)
    return `${words.slice(0, 2).join(", ")} and ${words.length - 2} more`;
  return `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
};

export const missingTitle = ({ gaps }: MissingFields) =>
  `${listWords(gaps.map(columnWords))} ${gaps.length === 1 ? "isn't" : "aren't"} in yet`;

export const missingImpact = (
  { gaps, blocked }: MissingFields,
  inputTitle: string | null,
) => {
  const where = `${gaps.length === 1 ? "It's" : "They're"} not in this period's ${inputTitle || "files"}`;
  return blocked.length
    ? `${where}, so ${listWords(blocked.map(columnWords))} can't be worked out.`
    : `${where}. Totals leave ${gaps.length === 1 ? "it" : "them"} out until ${gaps.length === 1 ? "it's" : "they're"} added.`;
};

export const missingQuestion = (
  { gaps }: Pick<MissingFields, "gaps">,
  record: string,
  period: string | null,
) =>
  `Why ${gaps.length === 1 ? "is" : "are"} ${listWords(gaps.map(columnWords))} missing for ${record}${period ? ` in ${period}` : ""}?`;

/** A figure that has a value, but counts fewer lines than the file has for it. */
export interface PartialBlank {
  column: ReportColumn;
  blank: number;
  total: number;
}

export const partialTitle = (partials: PartialBlank[]) => {
  if (partials.length !== 1)
    return `${listWords(partials.map(({ column }) => columnWords(column)))} have blank lines`;
  const [{ column, blank }] = partials;
  return `${columnWords(column)} has ${blank === 1 ? "a blank line" : `${blank} blank lines`}`;
};

export const partialImpact = (
  { column, blank, total }: PartialBlank,
  inputTitle: string | null,
) => {
  const name = columnWords(column);
  const where = inputTitle ? ` in this period's ${inputTitle}` : "";
  return `${blank} of ${name}'s ${total} lines${where} ${blank === 1 ? "is" : "are"} blank, so ${name} counts the other ${total - blank}.`;
};
